import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search as SearchIcon, Edit, X, Save, Trash2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';
import { DELIVERY_OPTIONS, deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchMember = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [duplicatingMember, setDuplicatingMember] = useState(null);
  const [originalMemberId, setOriginalMemberId] = useState('');
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [role, setRole] = useState('');
  const [sortColumn, setSortColumn] = useState('member_id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchCriteria, setSearchCriteria] = useState({
    database_type: '',
    member_type: '',
    customer_type: '',
    member_id: '',
    name: '',
    company_name: '',
    email: '',
    designation: '',
    status: '',
    district: '',
    state: '',
    contact_no: ''
  });

  useEffect(() => {
    fetchStates();
    const storedRole = localStorage.getItem('role');
    setRole(storedRole || '');
  }, []);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/all-unique-states`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/members/search`, searchCriteria);
      setSearchResults(response.data);
      
      if (response.data.length === 0) {
        toast.info('No members found matching the criteria');
      } else {
        toast.success(`Found ${response.data.length} member(s)`);
      }
    } catch (error) {
      console.error('Error searching members:', error);
      toast.error('Failed to search members');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedResults = () => {
    if (!sortColumn) return searchResults;

    return [...searchResults].sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      // Special handling for member_id - extract numeric part
      if (sortColumn === 'member_id') {
        const extractNumber = (id) => {
          const match = id.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        aVal = extractNumber(aVal);
        bVal = extractNumber(bVal);
      }
      // Handle date columns
      else if (sortColumn === 'created_at' || sortColumn === 'updated_at' || sortColumn === 'expiry_date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Handle string columns
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleReset = () => {
    setSearchCriteria({
      database_type: '',
      member_type: '',
      customer_type: '',
      member_id: '',
      name: '',
      company_name: '',
      email: '',
      designation: '',
      status: '',
      district: '',
      state: '',
      contact_no: ''
    });
    setSearchResults([]);
    setSortColumn('');
    setSortDirection('asc');
    setDistricts([]);
  };

  // Convert dd/mm/yyyy to yyyy-mm-dd for date input
  const convertToInputFormat = (dateStr) => {
    if (!dateStr) return '';
    try {
      // If already in yyyy-mm-dd format, return as is
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
      
      // Convert dd/mm/yyyy to yyyy-mm-dd
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      return '';
    } catch (e) {
      return '';
    }
  };

  // Convert yyyy-mm-dd to dd/mm/yyyy for storage
  const convertToStorageFormat = (dateStr) => {
    if (!dateStr) return '';
    try {
      // If already in dd/mm/yyyy format, return as is
      if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateStr;
      
      // Convert yyyy-mm-dd to dd/mm/yyyy
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
      return dateStr;
    } catch (e) {
      return dateStr;
    }
  };

  const fetchDistricts = async (state) => {
    if (!state) {
      setDistricts([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/all-unique-districts/${encodeURIComponent(state)}`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  const handleEdit = (member) => {
    // Store original member_id for update
    setOriginalMemberId(member.member_id);
    
    // Convert expiry_date to input format when loading for edit
    // Normalize status to uppercase if it exists
    const memberForEdit = {
      ...member,
      expiry_date: convertToInputFormat(member.expiry_date),
      status: member.status ? member.status.toUpperCase() : 'ACTIVE',
      delivery: deliveryLabel(member.delivery) || 'Registered Post'
    };
    setEditingMember(memberForEdit);
    // Fetch districts for the member's state
    if (member.state) {
      fetchDistricts(member.state);
    }
  };

  const handleDuplicate = async (member) => {
    // Fetch the new member ID first
    try {
      const response = await axios.get(`${API}/members/next-id`, {
        params: {
          magazine: member.database_type || 'Aabkari Times',
          customer: member.customer_type || 'Member',
          plan_type: member.plan_type || 'Registered Post',
          category: member.category || ''
        }
      });
      
      // Create a copy of the member for duplication with new ID
      const memberToDuplicate = {
        ...member,
        new_member_id: response.data.next_id,
        expiry_date: convertToInputFormat(member.expiry_date),
        status: member.status ? member.status.toUpperCase() : 'ACTIVE',
        delivery: deliveryLabel(member.delivery) || 'Registered Post',
        database_type: member.database_type || 'Aabkari Times',
        customer_type: member.customer_type || 'Member',
        plan_type: member.plan_type || 'Registered Post',
        subscription_duration: member.subscription_duration || '',
        amount_paid: member.amount_paid || '',
        notes: member.notes || '',
        category: member.category || ''
      };
      setDuplicatingMember(memberToDuplicate);
      if (member.state) {
        fetchDistricts(member.state);
      }
    } catch (error) {
      console.error('Error fetching next member ID:', error);
      toast.error('Failed to initialize duplicate. Please try again.');
    }
  };

  // Fetch new ID when database_type, customer_type, or plan_type changes in duplicate modal
  const fetchNewIdForDuplicate = async (magazine, customer, planType, category) => {
    try {
      const response = await axios.get(`${API}/members/next-id`, {
        params: {
          magazine: magazine,
          customer: customer,
          plan_type: planType || 'Registered Post',
          category: category || ''
        }
      });
      setDuplicatingMember(prev => ({ ...prev, new_member_id: response.data.next_id }));
    } catch (error) {
      console.error('Error fetching next member ID:', error);
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicatingMember(null);
  };

  const handleSaveDuplicate = async () => {
    if (!duplicatingMember.name) {
      toast.error('Name is required');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create as member with all fields
      const memberData = {
        database_type: duplicatingMember.database_type,
        member_type: duplicatingMember.member_type || '',
        customer_type: duplicatingMember.customer_type || 'Member',
        initials: duplicatingMember.initials || '',
        name: duplicatingMember.name,
        designation: duplicatingMember.designation || '',
        company_name: duplicatingMember.company_name || '',
        address: duplicatingMember.address || '',
        district: duplicatingMember.district || '',
        state: duplicatingMember.state || '',
        pincode: duplicatingMember.pincode || '',
        email: duplicatingMember.email || '',
        contact_no: duplicatingMember.contact_no || '',
        expiry_date: convertToStorageFormat(duplicatingMember.expiry_date),
        status: duplicatingMember.status || 'ACTIVE',
        plan_type: duplicatingMember.plan_type || '',
        delivery: deliveryLabel(duplicatingMember.delivery) || 'Registered Post',
        subscription_duration: duplicatingMember.subscription_duration || '',
        amount_paid: duplicatingMember.amount_paid || '',
        notes: duplicatingMember.notes || '',
        category: duplicatingMember.category || ''
      };
      
      await axios.post(`${API}/members`, memberData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Member duplicated successfully! New ID: ${duplicatingMember.new_member_id}`);
      
      setDuplicatingMember(null);
      // Refresh search results
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      console.error('Error duplicating member:', error);
      toast.error(error.response?.data?.detail || 'Failed to duplicate member');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Convert expiry_date back to dd/mm/yyyy before saving
      const memberToSave = {
        ...editingMember,
        expiry_date: convertToStorageFormat(editingMember.expiry_date)
      };
      
      // Use original member_id in URL to find the member
      await axios.put(`${API}/members/${originalMemberId}`, memberToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member updated successfully');
      setEditingMember(null);
      setOriginalMemberId('');
      // Refresh search results
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      console.error('Error updating member:', error);
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (memberId, memberName, magazine) => {
    if (!window.confirm(`Are you sure you want to delete member "${memberName}" (${magazine || 'unknown magazine'})? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = magazine ? `?magazine=${encodeURIComponent(magazine)}` : '';
      await axios.delete(`${API}/members/${memberId}${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member deleted successfully');
      // Refresh search results
      setSearchResults(searchResults.filter(m => !(m.member_id === memberId && (!magazine || m.database_type === magazine))));
    } catch (error) {
      console.error('Error deleting member:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to delete member');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center space-x-4 mb-8">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: 'var(--primary-blue)' }} data-testid="search-title">
              Search Member
            </h1>
          </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="form-section mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <Select value={searchCriteria.database_type} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, database_type: value })}>
                <SelectTrigger data-testid="database-type-filter">
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Customer Type</Label>
              <Select value={searchCriteria.customer_type} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, customer_type: value })}>
                <SelectTrigger data-testid="customer-type-filter">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Complimentary">Complimentary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Member ID</Label>
              <Input data-testid="member-id-input" value={searchCriteria.member_id} onChange={(e) => setSearchCriteria({ ...searchCriteria, member_id: e.target.value })} placeholder="Enter member ID" />
            </div>

            <div className="space-y-2">
              <Label>Name</Label>
              <Input data-testid="name-input" value={searchCriteria.name} onChange={(e) => setSearchCriteria({ ...searchCriteria, name: e.target.value })} placeholder="Enter name" />
            </div>

            <div className="space-y-2">
              <Label>Mobile Number</Label>
              <Input data-testid="mobile-input" value={searchCriteria.contact_no} onChange={(e) => setSearchCriteria({ ...searchCriteria, contact_no: e.target.value })} placeholder="Enter mobile number (partial)" />
            </div>

            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input data-testid="company-name-input" value={searchCriteria.company_name} onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })} placeholder="Enter company name" />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="email-input" value={searchCriteria.email} onChange={(e) => setSearchCriteria({ ...searchCriteria, email: e.target.value })} placeholder="Enter email" />
            </div>

            <div className="space-y-2">
              <Label>Designation</Label>
              <Input data-testid="designation-input" value={searchCriteria.designation} onChange={(e) => setSearchCriteria({ ...searchCriteria, designation: e.target.value })} placeholder="Enter designation" />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={searchCriteria.status} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, status: value })}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                  <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>State</Label>
              <Select value={searchCriteria.state || 'all_states'} onValueChange={(value) => {
                const newState = value === 'all_states' ? '' : value;
                setSearchCriteria({ ...searchCriteria, state: newState, district: '' });
                setDistricts([]);
                if (newState) fetchDistricts(newState);
              }}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_states">All States</SelectItem>
                  {states.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>District</Label>
              <Select value={searchCriteria.district || 'all_districts'} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, district: value === 'all_districts' ? '' : value })} disabled={!searchCriteria.state}>
                <SelectTrigger data-testid="district-select">
                  <SelectValue placeholder={searchCriteria.state ? 'All Districts' : 'Select state first'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_districts">All Districts</SelectItem>
                  {districts.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-8 flex space-x-4">
            <Button type="submit" disabled={loading} className="btn-primary" data-testid="search-button">
              <SearchIcon className="mr-2" size={18} />
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button type="button" onClick={handleReset} variant="outline" data-testid="reset-button">
              Reset
            </Button>
          </div>
        </form>

        {/* Edit Modal */}
        {editingMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ overflow: 'auto' }}>
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--primary-blue)' }}>Edit Member</h2>
                <Button variant="outline" onClick={handleCancelEdit} size="sm">
                  <X size={18} />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Member ID</Label>
                  <Input 
                    value={editingMember.member_id} 
                    onChange={(e) => setEditingMember({ ...editingMember, member_id: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Created Date</Label>
                  <Input 
                    value={editingMember.created_at ? new Date(editingMember.created_at).toLocaleString('en-IN') : 'N/A'} 
                    disabled 
                    className="bg-gray-100"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Last Modified Date</Label>
                  <Input 
                    value={editingMember.updated_at ? new Date(editingMember.updated_at).toLocaleString('en-IN') : 'N/A'} 
                    disabled 
                    className="bg-gray-100"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Initials</Label>
                  <Input value={editingMember.initials} onChange={(e) => setEditingMember({ ...editingMember, initials: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={editingMember.name} onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input value={editingMember.designation} onChange={(e) => setEditingMember({ ...editingMember, designation: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input value={editingMember.company_name} onChange={(e) => setEditingMember({ ...editingMember, company_name: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Contact Number</Label>
                  <Input value={editingMember.contact_no} onChange={(e) => setEditingMember({ ...editingMember, contact_no: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>WhatsApp Number</Label>
                  <Input 
                    value={editingMember.whatsapp_no || ''} 
                    onChange={(e) => setEditingMember({ ...editingMember, whatsapp_no: e.target.value.replace(/\D/g, '').slice(-10) })} 
                    placeholder="10-digit WhatsApp number"
                    maxLength={10}
                  />
                  <p className="text-xs text-gray-500">Country code +91 assumed</p>
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="text" value={editingMember.email} onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })} placeholder="Multiple emails separated by comma" />
                  <p className="text-xs text-gray-500">Comma-separated emails allowed</p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Address</Label>
                  <Input value={editingMember.address} onChange={(e) => setEditingMember({ ...editingMember, address: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>State</Label>
                  <Select 
                    value={editingMember.state} 
                    onValueChange={(value) => {
                      setEditingMember({ ...editingMember, state: value, district: '' });
                      fetchDistricts(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>District</Label>
                  <Input 
                    list="districts-edit"
                    value={editingMember.district} 
                    onChange={(e) => setEditingMember({ ...editingMember, district: e.target.value })} 
                    placeholder="Select or type district"
                  />
                  <datalist id="districts-edit">
                    {districts.map((district) => (
                      <option key={district} value={district} />
                    ))}
                  </datalist>
                </div>

                <div className="space-y-2">
                  <Label>Pincode (6 digits)</Label>
                  <Input 
                    type="text"
                    maxLength={6}
                    value={editingMember.pincode ? String(editingMember.pincode).replace('.0', '') : ''} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                      setEditingMember({ ...editingMember, pincode: value });
                    }} 
                  />
                </div>

                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Input type="date" value={editingMember.expiry_date} onChange={(e) => setEditingMember({ ...editingMember, expiry_date: e.target.value })} />
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={editingMember.status ? editingMember.status.toUpperCase() : 'ACTIVE'} 
                    onValueChange={(value) => setEditingMember({ ...editingMember, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                      <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Delivery</Label>
                  <Select 
                    value={deliveryLabel(editingMember.delivery) || 'Registered Post'} 
                    onValueChange={(value) => {
                      let updatedMemberId = editingMember.member_id;
                      
                      // Auto-update member_id based on delivery change
                      if (value === 'E-Magazine' && editingMember.member_id) {
                        // Change ATMR to ATME or STMR to STME
                        updatedMemberId = editingMember.member_id.replace(/ATMR/i, 'ATME').replace(/STMR/i, 'STME');
                      } else if (editingMember.delivery === 'E-Magazine' && value !== 'E-Magazine' && editingMember.member_id) {
                        // Change ATME back to ATMR or STME back to STMR
                        updatedMemberId = editingMember.member_id.replace(/ATME/i, 'ATMR').replace(/STME/i, 'STMR');
                      }
                      
                      setEditingMember({ ...editingMember, delivery: value, member_id: updatedMemberId });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select delivery method" />
                    </SelectTrigger>
                    <SelectContent>
                      {DELIVERY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-8 flex justify-end space-x-4">
                <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={loading} className="btn-primary">
                  <Save className="mr-2" size={18} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="form-section">
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--primary-blue)' }}>
              Search Results ({searchResults.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="search-results-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('member_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      ID {sortColumn === 'member_id' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Name {sortColumn === 'name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('designation')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Designation {sortColumn === 'designation' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('company_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Company {sortColumn === 'company_name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('expiry_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Expiry Date {sortColumn === 'expiry_date' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('contact_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Contact No. {sortColumn === 'contact_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('whatsapp_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      WhatsApp No. {sortColumn === 'whatsapp_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th onClick={() => handleSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Email {sortColumn === 'email' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedResults().map((member) => (
                    <tr key={member.id} data-testid={`member-row-${member.member_id}`}>
                      <td>{member.member_id}</td>
                      <td>{member.name}</td>
                      <td>{member.designation}</td>
                      <td>{member.company_name}</td>
                      <td>{member.expiry_date}</td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '120px' }}>
                        {member.contact_no || '-'}
                      </td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '120px' }}>
                        {member.whatsapp_no || '-'}
                      </td>
                      <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '180px', fontSize: '12px' }}>
                        {member.email || '-'}
                      </td>
                      <td>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(member)} data-testid={`edit-${member.member_id}`}>
                            <Edit size={16} className="mr-1" />
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDuplicate(member)} data-testid={`duplicate-${member.member_id}`} className="text-blue-600 border-blue-300 hover:bg-blue-50">
                            <Copy size={16} className="mr-1" />
                            Duplicate
                          </Button>
                          {role === 'admin' && (
                            <Button 
                              size="sm" 
                              variant="destructive" 
                              onClick={() => handleDelete(member.member_id, member.name, member.database_type)} 
                              data-testid={`delete-${member.member_id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Duplicate Modal - Full Form */}
        {duplicatingMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ overflow: 'auto' }}>
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-blue-600">
                  Duplicate Member
                </h2>
                <Button variant="ghost" size="sm" onClick={handleCancelDuplicate}>
                  <X size={20} />
                </Button>
              </div>
              
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-blue-700">
                      <strong>Original Member ID:</strong> {duplicatingMember.member_id}
                    </p>
                    <p className="text-sm text-blue-700">
                      <strong>Original Name:</strong> {duplicatingMember.name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-600">
                      New ID: {duplicatingMember.new_member_id || 'Calculating...'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Magazine/Database */}
                <div className="space-y-2">
                  <Label>Magazine/Database *</Label>
                  <Select 
                    value={duplicatingMember.database_type} 
                    onValueChange={(value) => {
                      setDuplicatingMember({ ...duplicatingMember, database_type: value });
                      fetchNewIdForDuplicate(value, duplicatingMember.customer_type, duplicatingMember.plan_type, duplicatingMember.category);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                      <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Customer Type */}
                <div className="space-y-2">
                  <Label>Customer Type *</Label>
                  <Select 
                    value={duplicatingMember.customer_type} 
                    onValueChange={(value) => {
                      setDuplicatingMember({ ...duplicatingMember, customer_type: value });
                      fetchNewIdForDuplicate(duplicatingMember.database_type, value, duplicatingMember.plan_type, duplicatingMember.category);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Complimentary">Complimentary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Plan Type */}
                <div className="space-y-2">
                  <Label>Plan Type</Label>
                  <Select 
                    value={duplicatingMember.plan_type || 'none'} 
                    onValueChange={(value) => {
                      const planVal = value === 'none' ? '' : value;
                      setDuplicatingMember({ ...duplicatingMember, plan_type: planVal });
                      fetchNewIdForDuplicate(duplicatingMember.database_type, duplicatingMember.customer_type, planVal, duplicatingMember.category);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select plan type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select --</SelectItem>
                      <SelectItem value="Registered Post">Registered Post</SelectItem>
                      <SelectItem value="E-Magazine">E-Magazine</SelectItem>
                      <SelectItem value="Member-Advt.">Member-Advt.</SelectItem>
                      <SelectItem value="Complimentary">Complimentary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Initials */}
                <div className="space-y-2">
                  <Label>Initials</Label>
                  <Input 
                    value={duplicatingMember.initials || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, initials: e.target.value })} 
                    placeholder="Mr., Mrs., Dr., etc."
                  />
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input 
                    value={duplicatingMember.name} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, name: e.target.value })} 
                  />
                </div>

                {/* Designation */}
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input 
                    value={duplicatingMember.designation || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, designation: e.target.value })} 
                  />
                </div>

                {/* Company Name */}
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input 
                    value={duplicatingMember.company_name || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, company_name: e.target.value })} 
                  />
                </div>

                {/* Contact Number */}
                <div className="space-y-2">
                  <Label>Contact Number</Label>
                  <Input 
                    value={duplicatingMember.contact_no || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, contact_no: e.target.value })} 
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    value={duplicatingMember.email || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, email: e.target.value })} 
                    placeholder="Multiple emails separated by comma"
                  />
                </div>

                {/* Address - Full Width */}
                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <Label>Address</Label>
                  <Input 
                    value={duplicatingMember.address || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, address: e.target.value })} 
                  />
                </div>

                {/* State */}
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select 
                    value={duplicatingMember.state || 'none'} 
                    onValueChange={(value) => {
                      const stateVal = value === 'none' ? '' : value;
                      setDuplicatingMember({ ...duplicatingMember, state: stateVal, district: '' });
                      if (stateVal) fetchDistricts(stateVal);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select --</SelectItem>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* District */}
                <div className="space-y-2">
                  <Label>District</Label>
                  <Input 
                    list="districts-duplicate"
                    value={duplicatingMember.district || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, district: e.target.value })} 
                    placeholder="Select or type district"
                  />
                  <datalist id="districts-duplicate">
                    {districts.map((district) => (
                      <option key={district} value={district} />
                    ))}
                  </datalist>
                </div>

                {/* Pincode */}
                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input 
                    type="text"
                    maxLength={6}
                    value={duplicatingMember.pincode ? String(duplicatingMember.pincode).replace('.0', '') : ''} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setDuplicatingMember({ ...duplicatingMember, pincode: value });
                    }} 
                  />
                </div>

                {/* Expiry Date */}
                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Input 
                    type="date" 
                    value={duplicatingMember.expiry_date || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, expiry_date: e.target.value })} 
                  />
                </div>

                {/* Subscription Duration */}
                <div className="space-y-2">
                  <Label>Subscription Duration</Label>
                  <Select 
                    value={duplicatingMember.subscription_duration || 'none'} 
                    onValueChange={(value) => setDuplicatingMember({ ...duplicatingMember, subscription_duration: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select --</SelectItem>
                      <SelectItem value="1">1 Year</SelectItem>
                      <SelectItem value="2">2 Years</SelectItem>
                      <SelectItem value="3">3 Years</SelectItem>
                      <SelectItem value="15">Life (15 Years)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Amount */}
                <div className="space-y-2">
                  <Label>Amount (₹)</Label>
                  <Input 
                    type="number"
                    value={duplicatingMember.amount_paid || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, amount_paid: e.target.value })} 
                    placeholder="Enter amount"
                  />
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={duplicatingMember.status || 'ACTIVE'} 
                    onValueChange={(value) => setDuplicatingMember({ ...duplicatingMember, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                      <SelectItem value="INACTIVE">INACTIVE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Delivery */}
                <div className="space-y-2">
                  <Label>Delivery</Label>
                  <Select 
                    value={deliveryLabel(duplicatingMember.delivery) || 'Registered Post'} 
                    onValueChange={(value) => setDuplicatingMember({ ...duplicatingMember, delivery: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DELIVERY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={duplicatingMember.category || 'none'} 
                    onValueChange={(value) => {
                      const catVal = value === 'none' ? '' : value;
                      setDuplicatingMember({ ...duplicatingMember, category: catVal });
                      fetchNewIdForDuplicate(duplicatingMember.database_type, duplicatingMember.customer_type, duplicatingMember.plan_type, catVal);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select --</SelectItem>
                      <SelectItem value="Life">Life</SelectItem>
                      <SelectItem value="Regular">Regular</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes - Full Width */}
                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <Label>Notes</Label>
                  <Input 
                    value={duplicatingMember.notes || ''} 
                    onChange={(e) => setDuplicatingMember({ ...duplicatingMember, notes: e.target.value })} 
                    placeholder="Any additional notes"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">
                <Button variant="outline" onClick={handleCancelDuplicate}>
                  Cancel
                </Button>
                <Button onClick={handleSaveDuplicate} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                  <Copy size={16} className="mr-2" />
                  {loading ? 'Creating...' : `Create as ${duplicatingMember.new_member_id || 'New Member'}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default SearchMember;